import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { GuidedLearningSession } from '../types';
import apiService from '../services/api';
import { 
  ArrowLeftIcon,
  PlayIcon,
  PauseIcon,
  StopIcon,
  TrashIcon,
  ChartBarIcon,
  ClockIcon,
  ChatBubbleLeftRightIcon,
  BookmarkIcon,
  EyeIcon
} from '@heroicons/react/24/outline';

const GuidedLearningDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  
  const [sessions, setSessions] = useState<GuidedLearningSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<'all' | 'active' | 'paused' | 'completed'>('all');

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    try {
      setIsLoading(true);
      const sessionsData = await apiService.getGuidedLearningSessions();
      setSessions(sessionsData);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load sessions');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResumeSession = (session: GuidedLearningSession) => {
    navigate(`/guided-learning/session/${session.id}`);
  };

  const handleDeleteSession = async (sessionId: string) => {
    if (!window.confirm('Are you sure you want to delete this session? This action cannot be undone.')) {
      return;
    }

    try {
      await apiService.deleteGuidedLearningSession(sessionId);
      setSessions(sessions.filter(s => s.id !== sessionId));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete session');
    }
  };

  const handleUpdateStatus = async (sessionId: string, status: 'active' | 'paused' | 'completed' | 'abandoned') => {
    try {
      await apiService.updateGuidedLearningSessionStatus(sessionId, { status });
      setSessions(sessions.map(s => 
        s.id === sessionId 
          ? { ...s, status, completedAt: status === 'completed' ? new Date().toISOString() : undefined }
          : s
      ));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update session status');
    }
  };

  const filteredSessions = sessions.filter(session => {
    if (filter === 'all') return true;
    return session.status === filter;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'paused': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'abandoned': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <PlayIcon className="h-4 w-4" />;
      case 'paused': return <PauseIcon className="h-4 w-4" />;
      case 'completed': return <StopIcon className="h-4 w-4" />;
      case 'abandoned': return <StopIcon className="h-4 w-4" />;
      default: return <ClockIcon className="h-4 w-4" />;
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Loading sessions...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/cases')}
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Cases
        </button>
        
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Guided Learning Sessions</h1>
            <p className="mt-2 text-gray-600">
              Manage your interactive learning sessions with AI-simulated patients and families
            </p>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {[
              { key: 'all', label: 'All Sessions', count: sessions.length },
              { key: 'active', label: 'Active', count: sessions.filter(s => s.status === 'active').length },
              { key: 'paused', label: 'Paused', count: sessions.filter(s => s.status === 'paused').length },
              { key: 'completed', label: 'Completed', count: sessions.filter(s => s.status === 'completed').length }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key as any)}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  filter === tab.key
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label} ({tab.count})
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Sessions Grid */}
      {filteredSessions.length === 0 ? (
        <div className="text-center py-12">
          <ChatBubbleLeftRightIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No sessions found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {filter === 'all' 
              ? 'Get started by creating a new guided learning session from a case.'
              : `No ${filter} sessions found.`
            }
          </p>
          <div className="mt-6">
            <button
              onClick={() => navigate('/cases')}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              <PlayIcon className="h-4 w-4 mr-2" />
              Start New Session
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredSessions.map((session) => (
            <div key={session.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              {/* Session Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    {session.case?.title || 'Unknown Case'}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {session.case?.patientProfile?.name || 'Unknown Patient'}
                  </p>
                </div>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(session.status)}`}>
                  {getStatusIcon(session.status)}
                  <span className="ml-1 capitalize">{session.status}</span>
                </span>
              </div>

              {/* Session Stats */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">{session.totalMessages}</div>
                  <div className="text-xs text-gray-500">Messages</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{session.currentCheckpoint}</div>
                  <div className="text-xs text-gray-500">Checkpoints</div>
                </div>
              </div>

              {/* Session Info */}
              <div className="space-y-2 mb-4">
                <div className="flex items-center text-sm text-gray-500">
                  <ClockIcon className="h-4 w-4 mr-2" />
                  Started: {new Date(session.createdAt).toLocaleDateString()}
                </div>
                <div className="flex items-center text-sm text-gray-500">
                  <ChatBubbleLeftRightIcon className="h-4 w-4 mr-2" />
                  Last activity: {new Date(session.lastActivityAt).toLocaleDateString()}
                </div>
                <div className="flex items-center text-sm text-gray-500">
                  <BookmarkIcon className="h-4 w-4 mr-2" />
                  Difficulty: {session.sessionMetadata.difficultyLevel}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-2">
                {session.status === 'active' ? (
                  <button
                    onClick={() => handleResumeSession(session)}
                    className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <PlayIcon className="h-4 w-4 mr-1" />
                    Continue
                  </button>
                ) : session.status === 'paused' ? (
                  <button
                    onClick={() => handleResumeSession(session)}
                    className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                  >
                    <PlayIcon className="h-4 w-4 mr-1" />
                    Resume
                  </button>
                ) : (
                  <button
                    onClick={() => handleResumeSession(session)}
                    className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <EyeIcon className="h-4 w-4 mr-1" />
                    Review
                  </button>
                )}

                {session.status === 'active' && (
                  <button
                    onClick={() => handleUpdateStatus(session.id, 'paused')}
                    className="px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <PauseIcon className="h-4 w-4" />
                  </button>
                )}

                {session.status === 'paused' && (
                  <button
                    onClick={() => handleUpdateStatus(session.id, 'completed')}
                    className="px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <StopIcon className="h-4 w-4" />
                  </button>
                )}

                <button
                  onClick={() => handleDeleteSession(session.id)}
                  className="px-3 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>

              {/* Progress Bar */}
              <div className="mt-4">
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>Progress</span>
                  <span>{Math.round((session.totalMessages / 50) * 100)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${Math.min(100, (session.totalMessages / 50) * 100)}%` }}
                  ></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GuidedLearningDashboardPage;
